import type { Price } from "$global-providers/rxdb-provider/collections/price";
import type { PriceListItemExternalReference } from "$global-providers/rxdb-provider/collections/price-list-item-external-reference";
import type { PriceListTariff } from "$global-providers/rxdb-provider/collections/price-list-tariff";
import type { PriceableItemOrigin } from "elephant-web/gql";
import type { RxCollection } from "rxdb";
import type { ResolvedPrice } from "./build-line-item";

export interface LookupPriceArgs {
  origin: PriceableItemOrigin;
  foreignId: string;
  /**
   * Price lists to search, in precedence order. Typically the facility's
   * own price list first, then the linked-organisation price list as
   * fallback. A facility that's been linked to an org price list via
   * `linkToOrganisationPriceList` gets an empty facility-own list and a
   * populated linked-org list, so both must be searched.
   *
   * Prices from all lists are merged before tariff selection so that a
   * Patient-tariff row on the linked-org list isn't masked by an
   * insurance-tariff row on the facility-own list. Within the same
   * tariff, the earlier list in this array wins (facility-own overrides
   * linked-org).
   */
  priceListIds: string[];
  externalReferenceCollection: RxCollection<PriceListItemExternalReference>;
  priceCollection: RxCollection<Price>;
  tariffCollection: RxCollection<PriceListTariff>;
}

const PATIENT_TARIFF_NAME = "Patient";

export const lookupPrice = async ({
  origin,
  foreignId,
  priceListIds,
  externalReferenceCollection,
  priceCollection,
  tariffCollection,
}: LookupPriceArgs): Promise<ResolvedPrice | null> => {
  if (priceListIds.length === 0) return null;

  const refs = await externalReferenceCollection
    .find({ selector: { origin, foreignId } })
    .exec();
  if (refs.length === 0) return null;

  for (const ref of refs) {
    // Gather prices from every priceListId, preserving precedence order
    // (facility-own first). `Array.prototype.find` will pick the
    // earliest-indexed price for any given predicate, so the order here
    // also encodes the "facility-own overrides linked-org for the same
    // tariff" rule.
    const pricesByList = await Promise.all(
      priceListIds.map((priceListId) =>
        priceCollection
          .find({ selector: { priceListId, itemId: ref.itemId } })
          .exec(),
      ),
    );
    const prices = pricesByList.flat();
    if (prices.length === 0) continue;

    const tariffIds = prices.map((p) => p.tariffId);
    const tariffs = await tariffCollection
      .find({ selector: { id: { $in: tariffIds } } })
      .exec();
    const patientTariff = tariffs.find((t) => t.name === PATIENT_TARIFF_NAME);

    const chosen =
      (patientTariff && prices.find((p) => p.tariffId === patientTariff.id)) ||
      prices[0];

    return {
      priceListItemId: ref.itemId,
      cost: chosen.value,
      revenueDepartmentId: chosen.revenueDepartmentId,
      tariffId: chosen.tariffId,
    };
  }

  return null;
};
